import { useState } from "react";
import blackDeleteIcon from "../assets/black-delete-icon.svg";
import saveIcon from "../assets/save-icon.svg";

const PROCEDURE_TRUNCATE_LENGTH = 48;

const gridColsClass =
  "grid grid-cols-[0.48fr_1.44fr_2.04fr_1.20fr_0.87fr_0.96fr_0.6fr] xl:gap-x-[16px] 2xl:gap-x-[30px] 3xl:gap-x-[35px] items-start";

const ChevronIcon = () => (
  <div className="shrink-0 pointer-events-none">
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
      <path
        d="M7 10.5L14 17.5L21 10.5"
        stroke="#5AC69F"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const ProcedureCell = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const value = text || "--";
  const shouldTruncate = value.length > PROCEDURE_TRUNCATE_LENGTH;
  const displayText =
    !shouldTruncate || expanded
      ? value
      : `${value.slice(0, PROCEDURE_TRUNCATE_LENGTH)}...`;

  return (
    <div className="min-w-0 pr-[12px] break-words font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
      {displayText}
      {shouldTruncate && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="ml-[4px] bg-transparent border-none p-0 font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#5AC69F] cursor-pointer"
        >
          {expanded ? "show less" : "show more"}
        </button>
      )}
    </div>
  );
};

const TreatmentPlanAi = () => {
  const [plans, setPlans] = useState([]);
  const [speciality, setSpeciality] = useState("");
  const [procedure, setProcedure] = useState("");
  const [daysUnits, setDaysUnits] = useState("");
  const [ichiCode, setIchiCode] = useState("");

  const handleAdd = () => {
    const trimmedSpeciality = speciality.trim();
    const trimmedProcedure = procedure.trim();
    const trimmedDaysUnits = daysUnits.trim();
    const trimmedIchiCode = ichiCode.trim();

    if (
      !trimmedSpeciality ||
      !trimmedProcedure ||
      !trimmedDaysUnits ||
      !trimmedIchiCode
    ) {
      return;
    }

    setPlans((prev) => [
      ...prev,
      {
        speciality: trimmedSpeciality,
        procedure: trimmedProcedure,
        daysUnits: trimmedDaysUnits,
        ichiCode: trimmedIchiCode,
        stratification: "NA",
      },
    ]);
    setSpeciality("");
    setProcedure("");
    setDaysUnits("");
    setIchiCode("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleDelete = (index) => {
    setPlans((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="w-full">
      <div className="w-full h-0 border border-solid border-[#00000010] bg-[#00000010]" />{" "}
      <div className="flex items-center justify-between gap-3 pt-[30px]">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative w-full min-w-0 box-border overflow-hidden h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent px-[32px] flex items-center">
            <input
              type="text"
              placeholder="Specialty"
              value={speciality}
              onChange={(e) => setSpeciality(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full min-w-0 bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:border-none shadow-none font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] placeholder:text-[#8B8B8B]"
            />
          </div>

          <div className="relative w-full min-w-0 box-border overflow-hidden h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent pl-[32px] pr-[10px] flex items-center">
            <input
              type="text"
              placeholder="Procedure"
              value={procedure}
              onChange={(e) => setProcedure(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full min-w-0 bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:border-none shadow-none font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] placeholder:text-[#8B8B8B]"
            />
            <ChevronIcon />
          </div>

          <div className="relative w-full min-w-0 box-border overflow-hidden h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent px-[32px] flex items-center">
            <input
              type="text"
              inputMode="numeric"
              placeholder="No. of Days/ Units"
              value={daysUnits}
              onChange={(e) =>
                setDaysUnits(e.target.value.replace(/[^0-9]/g, ""))
              }
              onKeyDown={handleKeyDown}
              className="w-full min-w-0 bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:border-none shadow-none font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] placeholder:text-[#8B8B8B]"
            />
          </div>

          <div className="relative w-full min-w-0 box-border overflow-hidden h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent pl-[32px] pr-[10px] flex items-center">
            <input
              type="text"
              placeholder="ICHI Code"
              value={ichiCode}
              onChange={(e) => setIchiCode(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full min-w-0 bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:border-none shadow-none font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] placeholder:text-[#8B8B8B]"
            />
            <ChevronIcon />
          </div>
        </div>

        <button
          type="button"
          onClick={handleAdd}
          className="shrink-0 w-[88px] h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B]"
        >
          ADD
        </button>
      </div>
      <div className="pt-[30px]">
        <div className={gridColsClass}>
          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            No.
          </div>

          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Specialty
          </div>

          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Procedure
          </div>

          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Stratification
          </div>

          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Days/Units
          </div>

          <div className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            ICHI Code
          </div>

          <div className="justify-self-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#5AC69F]">
            Action
          </div>
        </div>

        <div className="mt-[30px] flex flex-col gap-y-[34px]">
          {plans.map((item, index) => (
            <div
              key={`${item.speciality}-${item.procedure}-${index}`}
              className={gridColsClass}
            >
              <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
                {index + 1}.
              </div>

              <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
                {item.speciality || "--"}
              </div>

              <ProcedureCell text={item.procedure} />

              <div className="break-words font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
                {item.stratification || "NA"}
              </div>

              <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
                {item.daysUnits || "--"}
              </div>

              <div className="font-lufga font-normal text-[14px] leading-[100%] tracking-[-0.03em] text-[#000000]">
                {item.ichiCode || "--"}
              </div>

              <button
                type="button"
                onClick={() => handleDelete(index)}
                className="flex items-center justify-self-center bg-transparent border-none cursor-pointer"
              >
                <img src={blackDeleteIcon} alt="delete icon" />
              </button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end mt-[12px]">
        <button className="w-[170px] h-[74px] xl:w-[94.97px] xl:h-[40.97px]  2xl:w-[94.97px] 2xl:h-[40.97px] border border-none rounded-full bg-black flex justify-center items-center gap-[8px] font-lufga text-[14px] text-[#68FF9F] font-normal">
          SAVE
          <img src={saveIcon} alt="save" className="pl-[16px]" />
        </button>
      </div>
    </div>
  );
};

export default TreatmentPlanAi;
